import { useRef } from 'react';
import Router from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { CertificatesSelectTemplate } from 'components/admin/settings/certificates';
import { ModalButtonWithForm } from 'components/common';
import { ProductCustomAttributes } from 'components/common/customAttributes';
import { FormGroup, Text } from 'components/common/form';

import type { Path } from 'shared-types';

type FormValues = Pick<Path, 'customAttributes' | 'title'>;

const PathsCreate = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: {
      certificatesEnableSelection,
      certificatesEnableSelectionOnCreate,
      pathCustomAttributes
    }
  } = useReduxState();

  const titleInputContainer = useRef<HTMLDivElement>(undefined);

  const handleModalVisible = () => {
    titleInputContainer.current.querySelector('input').focus();
  };

  const submit = async (values: FormValues) => {
    try {
      const response = await api.post('/admin/paths', values);

      dispatch(
        actions.addNotice({
          text: messages.pathsCreateSuccess,
          type: 'success'
        })
      );

      Router.push(
        '/admin/paths/[pathId]',
        `/admin/paths/${response.data.path.id}`
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateTitle = async (value: string) => {
    if (!value) return messages.commonTitleEmpty;

    try {
      await api.head(`/admin/paths?title=${value}`);
      return messages.commonTitleInUse;
    } catch (err) {
      return undefined;
    }
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonText={messages.pathsCreate}
      initialValues={{ CertificateTemplateId: 0 }}
      modalActionText={messages.commonCreate}
      modalTitle={messages.pathsCreate}
      onModalVisible={handleModalVisible}
      onSubmit={submit}
      pristineIsValid={false}
    >
      {() => (
        <FormGroup htmlFor="title" label={messages.commonTitle}>
          <Text
            customValidateAsync={validateTitle}
            field="title"
            id="title"
            inputContainerRef={(el: HTMLDivElement) =>
              (titleInputContainer.current = el)
            }
          />

          {certificatesEnableSelection &&
            certificatesEnableSelectionOnCreate && (
              <CertificatesSelectTemplate type="path" />
            )}

          {pathCustomAttributes?.some(
            (attr) => attr.required || attr.displayInCreateForm
          ) ? (
            <div style={{ marginTop: 30 }}>
              <ProductCustomAttributes
                hideWarnings={true}
                renderOnlyRequiredOrDisplayable={true}
                type="path"
              />
            </div>
          ) : null}
        </FormGroup>
      )}
    </ModalButtonWithForm>
  );
};

export default PathsCreate;
